import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, AlertTriangle, Loader2, Flame, Clock, PauseCircle, CheckCircle2, Archive, AlertCircle, Plus, Paperclip, X, Building2, User, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from "@/components/ui/context-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { SwipeDeleteRow } from "@/pages/pratiche/SwipeDeleteRow";
import { URGENCY, STATUS_LABELS, catLabel, CATEGORY_LABELS } from "@/pages/pratiche/constants";
import { PraticaDetail } from "@/pages/pratiche/PraticaDetail";
import PageTour from "@/components/PageTour";

const UrgencyBadge = ({ level }) => {
  const u = URGENCY[level] || URGENCY.normale;
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${u.cls}`}>{u.label}</span>;
};

const IT_MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const monthKey = (iso) => (iso || "").slice(0, 7);
const monthLabel = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${IT_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};
const closedDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};
const openedDateTimeLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const KpiCard = ({ icon: Icon, label, value, tone = "default", active, onClick, testid }) => {
  const tones = {
    danger: "text-red-600", warn: "text-orange-500", info: "text-[#007BFF]", muted: "text-muted-foreground",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex-1 min-w-[100px] bg-card border rounded-lg px-3 py-2 text-left transition-colors ${active ? "border-[#007BFF] ring-1 ring-[#007BFF]" : "border-border hover:border-[#007BFF]/40"}`}>
      <div className="flex items-center gap-1.5">
        <Icon className={`h-4 w-4 ${tones[tone] || tones.info}`} />
        <span className={`text-xl font-extrabold ${tones[tone] || ""}`}>{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
    </button>
  );
};

export default function Pratiche() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("tutti");
  const [urgencyFilter, setUrgencyFilter] = useState("tutti");
  const [buildingFilter, setBuildingFilter] = useState("tutti");
  const [periodFilter, setPeriodFilter] = useState("tutti");
  const [sortBy, setSortBy] = useState("apertura");
  const [sortDir, setSortDir] = useState("desc");
  const [buildings, setBuildings] = useState([]);
  const [attention, setAttention] = useState(true);
  const [toArchive, setToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // Creazione manuale pratica
  const [showNew, setShowNew] = useState(false);
  const [nbBuilding, setNbBuilding] = useState("");
  const [nbResidents, setNbResidents] = useState([]);
  const [nbResidentId, setNbResidentId] = useState("__manual__");
  const [nbSender, setNbSender] = useState("");
  const [nbEmail, setNbEmail] = useState("");
  const [nbPhone, setNbPhone] = useState("");
  const [nbProblem, setNbProblem] = useState("");
  const [nbPlace, setNbPlace] = useState("");
  const [nbCategory, setNbCategory] = useState("");
  const [nbPriority, setNbPriority] = useState("media");
  const [nbFiles, setNbFiles] = useState([]);
  const [nbUploading, setNbUploading] = useState(false);
  const [nbSaving, setNbSaving] = useState(false);

  const resetNew = () => {
    setNbBuilding(""); setNbResidents([]); setNbResidentId("__manual__"); setNbSender("");
    setNbEmail(""); setNbPhone(""); setNbProblem(""); setNbPlace(""); setNbCategory("");
    setNbPriority("media"); setNbFiles([]);
  };

  const onNbBuilding = async (bid) => {
    setNbBuilding(bid);
    setNbResidentId("__manual__"); setNbSender(""); setNbEmail(""); setNbPhone("");
    try { const r = await api.get(`/buildings/${bid}/residents`); setNbResidents(r.data || []); }
    catch { setNbResidents([]); }
  };

  const onNbResident = (rid) => {
    setNbResidentId(rid);
    if (rid === "__manual__") { setNbSender(""); setNbEmail(""); setNbPhone(""); return; }
    const r = nbResidents.find((x) => x.id === rid);
    if (r) { setNbSender(r.nome || ""); setNbEmail(r.email || ""); setNbPhone(r.telefono || ""); }
  };

  const uploadNbFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)."); e.target.value = ""; return; }
    setNbUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/pratiche/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNbFiles((a) => [...a, { file_id: r.data.file_id, filename: r.data.filename }]);
    } catch { toast.error("Caricamento allegato non riuscito"); }
    finally { setNbUploading(false); e.target.value = ""; }
  };

  const createManual = async () => {
    if (!nbBuilding) { toast.error("Seleziona il condominio."); return; }
    if (!nbSender.trim()) { toast.error("Indica il segnalante."); return; }
    if (!nbProblem.trim()) { toast.error("Scrivi il motivo della segnalazione."); return; }
    setNbSaving(true);
    try {
      const r = await api.post("/pratiche/manual", {
        building_id: nbBuilding, sender_name: nbSender.trim(), problem: nbProblem.trim(),
        place: nbPlace.trim(), category: nbCategory, priority: nbPriority,
        resident_id: nbResidentId === "__manual__" ? "" : nbResidentId,
        sender_email: nbEmail.trim(), wa_contact: nbPhone.trim(),
        attachment_ids: nbFiles.map((f) => f.file_id),
        origin_url: window.location.origin,
      });
      toast.success(`Pratica ${r.data.ref} creata`);
      if (r.data.notified) toast.success("Notifica inviata al condòmino con il link al portale");
      setShowNew(false); resetNew();
      setStatusFilter("tutti"); setAttention(false);
      load();
      if (r.data.id) setSelectedId(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione pratica non riuscita"); }
    finally { setNbSaving(false); }
  };

  useEffect(() => { api.get("/buildings").then((r) => setBuildings(r.data || [])).catch(() => {}); }, []);

  const loadDash = useCallback(async () => {
    try { const r = await api.get("/pratiche/dashboard"); setDash(r.data); } catch (e) { console.debug("pratiche dashboard:", e); }
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const useAttention = attention && (statusFilter === "tutti" || statusFilter === "aperte");
      const params = { attention: useAttention || undefined };
      if (statusFilter !== "tutti") params.status = statusFilter;
      if (urgencyFilter !== "tutti") params.priority = urgencyFilter;
      if (buildingFilter !== "tutti") params.building_id = buildingFilter;
      const r = await api.get("/pratiche", { params });
      setItems(r.data || []);
    } catch (e) { console.debug("pratiche list:", e); } finally { if (!silent) setLoading(false); }
    loadDash();
  }, [statusFilter, urgencyFilter, buildingFilter, attention, loadDash]);

  useEffect(() => { load(); }, [load]);
  // Aggiornamento push: ricarica silenziosa ogni 8s (nessun bottone di refresh)
  useEffect(() => {
    const t = setInterval(() => load(true), 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (statusFilter !== "chiuse") setPeriodFilter("tutti"); }, [statusFilter]);

  // Apre una pratica e la segna come LETTA (come la posta). Aggiorna il badge in sidebar.
  const openPratica = useCallback((p) => {
    setSelectedId(p.id);
    if (!p.seen && !p.is_demo) {
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, seen: true } : x)));
      api.post(`/pratiche/${p.id}/seen`)
        .then(() => window.dispatchEvent(new Event("pratiche-updated")))
        .catch(() => {});
    }
  }, []);

  // --- Tour guidato: crea una pratica DEMO e la elimina alla fine ---
  const demoIdRef = useRef(null);
  const startTourDemo = useCallback(async () => {
    try {
      const r = await api.post("/pratiche/tour-demo");
      demoIdRef.current = r.data.id;
      setStatusFilter("tutti"); setUrgencyFilter("tutti"); setBuildingFilter("tutti"); setAttention(false);
      await load(true);
      setSelectedId(r.data.id);
    } catch (e) { console.debug("tour demo create:", e); }
  }, [load]);
  const endTourDemo = useCallback(async () => {
    try { await api.delete("/pratiche/tour-demo"); } catch (e) { console.debug("tour demo delete:", e); }
    if (demoIdRef.current) {
      setSelectedId((cur) => (cur === demoIdRef.current ? null : cur));
      demoIdRef.current = null;
    }
    load(true);
  }, [load]);

  const PRATICHE_TOUR_STEPS = [
    { title: "Benvenuto in Pratiche AI 📋", body: "Il tuo direttore operativo: raccoglie le segnalazioni, calcola l'urgenza e prepara le bozze. Tu approvi. Ho creato una pratica di esempio per mostrartelo — la elimino a fine guida." },
    { selector: "kpi-aperte", title: "Il colpo d'occhio", body: "In alto vedi subito emergenze, pratiche aperte, urgenti e quelle ferme da troppo tempo. Tocca una scheda per filtrare l'elenco." },
    { selector: "pratica-card-demo", title: "Una pratica", body: "Ogni riga è una segnalazione con urgenza, condominio e stato. Le più urgenti salgono in cima automaticamente." },
    { selector: "pratica-motivo", title: "Il problema segnalato", body: "Qui trovi la descrizione del problema. L'AI la analizza per stimare categoria, urgenza ed eventuali rischi." },
    { selector: "pratica-condomino-draft", title: "Bozza per il condòmino", body: "L'AI prepara già una risposta pronta da inviare al condòmino via email o SMS (max 160 caratteri). La puoi modificare prima di approvare." },
    { selector: "pratica-intervention-draft", title: "Richiesta al fornitore", body: "E prepara anche la richiesta di intervento per il fornitore. Nulla parte senza la tua approvazione." },
    { selector: "new-pratica-btn", title: "Crea una pratica", body: "Puoi aprire una pratica a mano da qui. Molte si aprono da sole dalle segnalazioni urgenti che arrivano via email." },
    { title: "Tutto qui! 🎉", body: "Elimino la pratica di esempio. Riavvia questa guida quando vuoi dal pulsante \"Guida\" in alto." },
  ];


  // Deep-link: /pratiche?id=... preseleziona la pratica
  useEffect(() => {
    const qid = searchParams.get("id");
    if (qid) { setSelectedId(qid); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const doArchive = async () => {
    if (!toArchive) return;
    setArchiving(true);
    try {
      await api.post(`/pratiche/${toArchive.id}/archive`);
      setItems((prev) => prev.filter((x) => x.id !== toArchive.id));
      if (selectedId === toArchive.id) setSelectedId(null);
      toast.success("Pratica chiusa e archiviata");
      setToArchive(null);
      loadDash();
    } catch {
      toast.error("Errore durante l'archiviazione della pratica");
    } finally {
      setArchiving(false);
    }
  };

  const c = (dash || {}).counters || {};
  const showPeriod = statusFilter === "chiuse";
  const monthOptions = showPeriod
    ? Array.from(new Set(items.map((p) => monthKey(p.closed_at)).filter(Boolean))).sort().reverse()
    : [];
  const displayItems = (showPeriod && periodFilter !== "tutti")
    ? items.filter((p) => monthKey(p.closed_at) === periodFilter)
    : items;
  const PRIO_RANK = { emergenza: 4, alta: 3, media: 2, bassa: 1 };
  const sortedItems = [...displayItems].sort((a, b) => {
    let cmp = 0;
    if (sortBy === "priorita") {
      const ra = PRIO_RANK[(a.urgency || {}).level] || 0;
      const rb = PRIO_RANK[(b.urgency || {}).level] || 0;
      cmp = ra - rb;
      if (cmp === 0) cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
    } else if (sortBy === "cronologico") {
      cmp = new Date(a.updated_at || a.created_at || 0) - new Date(b.updated_at || b.created_at || 0);
    } else {
      cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)] md:h-[100dvh]" data-testid="pratiche-page">
      {/* Dashboard attenzione (Modulo 14) — nascosta su mobile quando si apre una pratica */}
      <div className={`px-5 pt-4 pb-3 border-b border-border bg-background ${selectedId ? "hidden md:block" : ""}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#007BFF]" />
            <h1 className="font-head text-lg font-extrabold tracking-tight">Pratiche AI</h1>
            <span className="text-xs text-muted-foreground hidden sm:inline">· il direttore operativo: prepara, tu approvi</span>
          </div>
          <div className="flex items-center gap-3">
            <PageTour storageKey="tour_pratiche" steps={PRATICHE_TOUR_STEPS} onOpen={startTourDemo} onClose={endTourDemo} />
            <button data-testid="new-pratica-btn" onClick={() => { resetNew(); setShowNew(true); }}
              className="inline-flex items-center gap-1.5 bg-[#007BFF] hover:bg-[#0056B3] text-white text-xs font-semibold rounded-lg px-3 py-2 transition-colors">
              <Plus className="h-4 w-4" />Nuova pratica
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <KpiCard testid="kpi-emergenze" icon={Flame} tone="danger" label="Emergenze" value={c.emergenze || 0} active={urgencyFilter === "emergenza"} onClick={() => { setUrgencyFilter(urgencyFilter === "emergenza" ? "tutti" : "emergenza"); setStatusFilter("aperte"); }} />
          <KpiCard testid="kpi-aperte" icon={ClipboardList} tone="info" label="Aperte" value={c.aperte || 0} onClick={() => { setStatusFilter("aperte"); setUrgencyFilter("tutti"); }} />
          <KpiCard testid="kpi-chiuse" icon={CheckCircle2} tone="muted" label="Chiuse" value={c.chiuse || 0} active={statusFilter === "chiuse"} onClick={() => { setStatusFilter(statusFilter === "chiuse" ? "tutti" : "chiuse"); setAttention(false); setUrgencyFilter("tutti"); }} />
          <KpiCard testid="kpi-urgenti" icon={AlertTriangle} tone="warn" label="Urgenti aperte" value={c.urgenti_aperte || 0} onClick={() => { setStatusFilter("aperte"); setUrgencyFilter("tutti"); }} />
          <KpiCard testid="kpi-ferme" icon={PauseCircle} tone="muted" label="Ferme 7gg+" value={c.ferme || 0} onClick={() => { setStatusFilter("aperte"); setAttention(true); }} />
        </div>
        {(dash || {}).kpi && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground" data-testid="pratiche-kpi">
            <span>Risolte: <b className="text-foreground">{dash.kpi.pct_risolte}%</b></span>
            {dash.kpi.tempo_medio_risoluzione_gg != null && <span>Tempo medio risoluzione: <b className="text-foreground">{dash.kpi.tempo_medio_risoluzione_gg} gg</b></span>}
            <span>Aperte in ritardo: <b className="text-foreground">{dash.kpi.pct_in_ritardo}%</b></span>
            <span>Riaperte: <b className="text-foreground">{dash.kpi.riaperte}</b></span>
          </div>
        )}
      </div>

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
      {/* Lista */}
      <div className={`w-full md:w-[380px] shrink-0 border-r border-border flex-col bg-background ${selectedId ? "hidden md:flex" : "flex"}`}>
        <div className="px-4 py-3 border-b border-border flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid="pratiche-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti" className="text-xs">Tutti gli stati</SelectItem>
                <SelectItem value="aperte" className="text-xs">Solo aperte</SelectItem>
                <SelectItem value="chiuse" className="text-xs">Solo chiuse</SelectItem>
                <SelectItem value="archiviate" className="text-xs">Solo archiviate</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
              <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="pratiche-urgency-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti" className="text-xs">Ogni urgenza</SelectItem>
                <SelectItem value="emergenza" className="text-xs">Emergenza</SelectItem>
                <SelectItem value="alta" className="text-xs">Alta</SelectItem>
                <SelectItem value="normale" className="text-xs">Normale</SelectItem>
                <SelectItem value="bassa" className="text-xs">Bassa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Select value={buildingFilter} onValueChange={setBuildingFilter}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid="pratiche-building-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="tutti" className="text-xs">Tutti i condomìni</SelectItem>
                {buildings.map((b) => <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid="pratiche-sort-by"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="apertura" className="text-xs">Ordine di apertura</SelectItem>
                <SelectItem value="cronologico" className="text-xs">Ordine cronologico</SelectItem>
                <SelectItem value="priorita" className="text-xs">Priorità</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortDir} onValueChange={setSortDir}>
              <SelectTrigger className="h-8 text-xs w-[52px] shrink-0 justify-center" data-testid="pratiche-sort-dir" title={sortDir === "asc" ? "Dal basso" : "Dall'alto"}><SelectValue /></SelectTrigger>
              <SelectContent className="min-w-0">
                <SelectItem value="desc" className="text-xs">↓</SelectItem>
                <SelectItem value="asc" className="text-xs">↑</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {showPeriod && (
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="h-8 text-xs" data-testid="pratiche-period-filter"><SelectValue placeholder="Tutti i periodi" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="tutti" className="text-xs">Tutti i periodi</SelectItem>
                {monthOptions.map((k) => <SelectItem key={k} value={k} className="text-xs">{monthLabel(k)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : displayItems.length === 0 ? (
            <div className="px-5 py-16 text-center text-sm text-muted-foreground">
              Nessuna pratica. Le pratiche si aprono automaticamente dalle segnalazioni urgenti, oppure manualmente da una comunicazione.
            </div>
          ) : sortedItems.map((p) => (
            <SwipeDeleteRow key={p.id} item={p} onDelete={setToArchive} testid={`pratica-swipe-${p.id}`}>
            <ContextMenu>
              <ContextMenuTrigger asChild>
              <button
                data-testid={p.is_demo ? "pratica-card-demo" : `pratica-card-${p.id}`}
                onClick={() => openPratica(p)}
                className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${selectedId === p.id ? "bg-[#007BFF]/10 border-l-2 border-l-[#007BFF]" : "hover:bg-secondary/60"}`}
              >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
                  {!p.seen && !p.is_demo && (
                    <span data-testid={`pratica-unread-dot-${p.id}`} className="h-2 w-2 rounded-full bg-[#007BFF] shrink-0" title="Non letta" />
                  )}
                  {p.ref}
                </span>
                <div className="flex items-center gap-1.5">
                  {["chiusa", "annullata"].includes(p.status) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-700 text-white" data-testid={`pratica-closed-badge-${p.id}`}>{p.status === "annullata" ? "Annullata" : "Chiusa"}</span>}
                  {p.condomino_msg_unseen && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Richiesta</span>}
                  {p.overdue && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Ritardo</span>}
                  <UrgencyBadge level={(p.urgency || {}).level} />
                </div>
              </div>
              <div className={`text-sm truncate ${!p.seen && !p.is_demo ? "font-bold text-foreground" : "font-semibold"}`}>{p.subject}</div>
              {p.problem && (
                <div className="mt-1 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200/70 px-2 py-1" data-testid={`pratica-motivo-${p.id}`}>
                  <AlertCircle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
                  <span className="text-[11px] text-amber-800 leading-snug line-clamp-2">{p.problem}</span>
                </div>
              )}
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span className="truncate">{catLabel(p.category)}</span>
                {(p.condo || {}).building_name && <span className="truncate">· {p.condo.building_name}</span>}
              </div>
              {p.created_at && (
                <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground/80" data-testid={`pratica-opened-${p.id}`}>
                  <Clock className="h-3 w-3 shrink-0" />
                  <span>Aperta il {openedDateTimeLabel(p.created_at)}</span>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center justify-between">
                <span>{STATUS_LABELS[p.status] || p.status}</span>
                {["chiusa", "annullata"].includes(p.status) && p.closed_at ? (
                  <span className="text-muted-foreground ml-2 shrink-0" data-testid={`pratica-closed-date-${p.id}`}>Chiusa il {closedDateLabel(p.closed_at)}</span>
                ) : (p.next_action && <span className="text-[#007BFF] truncate ml-2 max-w-[55%]" title={p.next_action}>→ {p.next_action}</span>)}
              </div>
            </button>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-52">
                <ContextMenuItem data-testid={`ctx-archive-pratica-${p.id}`} onClick={() => setToArchive(p)}>
                  <Archive className="h-4 w-4 mr-2" />{p.archived ? "Archiviata" : "Chiudi e archivia"}
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
            </SwipeDeleteRow>
          ))}
        </div>
      </div>

      {/* Dettaglio */}
      <div className={`flex-1 overflow-y-auto bg-secondary/30 ${selectedId ? "" : "hidden md:block"}`}>
        {selectedId ? (
          <PraticaDetail id={selectedId} onChange={load} onClose={() => setSelectedId(null)} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
            <ClipboardList className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Seleziona una pratica per vedere la scheda completa,<br />le proposte dell'AI e le azioni da approvare.</p>
            <p className="text-xs mt-4 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> L'AI prepara e propone. Nessun invio parte senza la tua approvazione.</p>
          </div>
        )}
      </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={() => !nbSaving && setShowNew(false)} data-testid="new-pratica-dialog">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg my-8 shadow-2xl ring-1 ring-black/5 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 bg-[#007BFF]/10 border-b border-[#007BFF]/20">
              <h2 className="font-head text-lg font-extrabold tracking-tight flex items-center gap-2 text-foreground">
                <span className="h-8 w-8 rounded-lg bg-[#007BFF] text-white flex items-center justify-center shrink-0"><Plus className="h-4 w-4" /></span>
                Nuova pratica
              </h2>
              <button onClick={() => setShowNew(false)} className="p-1.5 rounded-md text-muted-foreground hover:bg-background hover:text-foreground transition-colors" data-testid="new-pratica-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5">
            <p className="text-xs text-muted-foreground mb-4">Registra una segnalazione manualmente. L'AI calcolerà categoria, urgenza e bozze. I campi con <span className="text-red-500 font-semibold">*</span> sono obbligatori.</p>

            <label className="text-xs font-semibold tracking-wide text-foreground/75 flex items-center gap-1 mb-1"><Building2 className="h-3 w-3" />Condominio <span className="text-red-500">*</span></label>
            <Select value={nbBuilding} onValueChange={onNbBuilding}>
              <SelectTrigger className="h-9 text-sm mb-3" data-testid="nb-building"><SelectValue placeholder="Seleziona condominio" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {buildings.map((b) => <SelectItem key={b.id} value={b.id} className="text-sm">{b.name}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="text-xs font-semibold tracking-wide text-foreground/75 flex items-center gap-1 mb-1"><User className="h-3 w-3" />Segnalante <span className="text-red-500">*</span></label>
            <Select value={nbResidentId} onValueChange={onNbResident} disabled={!nbBuilding}>
              <SelectTrigger className="h-9 text-sm mb-2" data-testid="nb-resident"><SelectValue placeholder={nbBuilding ? "Scegli dal condominio o inserisci manualmente" : "Prima scegli il condominio"} /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__manual__" className="text-sm">Altro / inserisci manualmente</SelectItem>
                {nbResidents.map((r) => <SelectItem key={r.id} value={r.id} className="text-sm">{r.nome}{r.ruolo ? ` · ${r.ruolo}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {nbResidentId === "__manual__" && (
              <input data-testid="nb-sender-name" value={nbSender} onChange={(e) => setNbSender(e.target.value)} placeholder="Nome del segnalante *"
                className="w-full mb-2 bg-background border border-input rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20" />
            )}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <input data-testid="nb-email" value={nbEmail} onChange={(e) => setNbEmail(e.target.value)} placeholder="Email (facoltativa)"
                className="bg-background border border-input rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20" />
              <input data-testid="nb-phone" value={nbPhone} onChange={(e) => setNbPhone(e.target.value)} placeholder="Cellulare (facolt.)"
                className="bg-background border border-input rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20" />
            </div>

            <label className="text-xs font-semibold tracking-wide text-foreground/75 mb-1 block">Motivo della segnalazione <span className="text-red-500">*</span></label>
            <textarea data-testid="nb-problem" value={nbProblem} onChange={(e) => setNbProblem(e.target.value)} rows={4}
              placeholder="Descrivi il problema (es. perdita d'acqua in garage, ascensore fermo, ecc.)…"
              className="w-full resize-none bg-background border border-input rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20 mb-3" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <div>
                <label className="text-xs font-semibold tracking-wide text-foreground/75 flex items-center gap-1 mb-1"><MapPin className="h-3 w-3" />Luogo (facolt.)</label>
                <input data-testid="nb-place" value={nbPlace} onChange={(e) => setNbPlace(e.target.value)} placeholder="Es. Scala B, 3° piano…"
                  className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20" />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wide text-foreground/75 mb-1 block">Priorità (facolt.)</label>
                <Select value={nbPriority} onValueChange={setNbPriority}>
                  <SelectTrigger className="h-9 text-sm" data-testid="nb-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bassa" className="text-sm">Bassa</SelectItem>
                    <SelectItem value="media" className="text-sm">Normale</SelectItem>
                    <SelectItem value="alta" className="text-sm">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <label className="text-xs font-semibold tracking-wide text-foreground/75 mb-1 block">Categoria (facolt. — se vuota la stima l'AI)</label>
            <Select value={nbCategory || "__auto__"} onValueChange={(v) => setNbCategory(v === "__auto__" ? "" : v)}>
              <SelectTrigger className="h-9 text-sm mb-3" data-testid="nb-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__auto__" className="text-sm">Automatica (AI)</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k} className="text-sm">{v}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="inline-flex items-center gap-1.5 text-xs border border-dashed border-border rounded-lg px-3 py-2 cursor-pointer hover:border-[#007BFF] transition-colors mb-2" data-testid="nb-attach-label">
              {nbUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              {nbUploading ? "Caricamento…" : "Allega foto o documenti (facolt.)"}
              <input type="file" className="hidden" accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp" onChange={uploadNbFile} disabled={nbUploading} data-testid="nb-attach-input" />
            </label>
            {nbFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="nb-files">
                {nbFiles.map((f) => (
                  <span key={f.file_id} className="flex items-center gap-1.5 text-xs bg-secondary border border-border rounded-md px-2 py-1">
                    <Paperclip className="h-3 w-3 text-[#007BFF]" /><span className="truncate max-w-[140px]">{f.filename}</span>
                    <button onClick={() => setNbFiles((a) => a.filter((x) => x.file_id !== f.file_id))} className="hover:text-red-500" data-testid={`nb-file-remove-${f.file_id}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowNew(false)} disabled={nbSaving} className="text-sm px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground">Annulla</button>
              <button data-testid="nb-submit" onClick={createManual} disabled={nbSaving || nbUploading || !nbBuilding || !nbSender.trim() || !nbProblem.trim()}
                className="text-sm px-4 py-2 rounded-lg bg-[#007BFF] hover:bg-[#0056B3] text-white font-semibold disabled:opacity-50 flex items-center gap-2">
                {nbSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Creazione…</> : <><Plus className="h-4 w-4" />Crea pratica</>}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!toArchive} onOpenChange={(o) => !o && setToArchive(null)}>
        <AlertDialogContent data-testid="archive-pratica-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archiviare la pratica?</AlertDialogTitle>
            <AlertDialogDescription>
              La pratica <b>{toArchive?.ref}</b> — "{toArchive?.subject}" verrà <b>chiusa e archiviata</b>. La ritrovi nel filtro "Solo archiviate" e potrai riaprirla quando vuoi. Le email collegate restano nelle comunicazioni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-archive-pratica" onClick={(e) => { e.preventDefault(); doArchive(); }} disabled={archiving} className="rounded-md bg-amber-600 hover:bg-amber-700">
              {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Archivia"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

